import React, { useRef } from 'react';
import { ContractDocument } from '../../types';
import { API_CONFIG } from '../../config/api.config';

interface ContractDocumentsSectionProps {
  existingDocuments: ContractDocument[];
  newFiles: File[];
  onRemoveExisting: (index: number) => void;
  onAddFiles: (files: File[]) => void;
  onRemoveNewFile: (index: number) => void;
}

const ContractDocumentsSection: React.FC<ContractDocumentsSectionProps> = ({
  existingDocuments,
  newFiles,
  onRemoveExisting,
  onAddFiles,
  onRemoveNewFile,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) onAddFiles(files);
    e.target.value = '';
  };

  const fileUrl = (doc: ContractDocument) =>
    doc.url?.startsWith('http') ? doc.url : `${API_CONFIG.BASE_URL}${doc.url}`;

  return (
    <div className="border-t border-gray-200 pt-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Contract Documents</h3>

      <div
        onClick={() => inputRef.current?.click()}
        className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-ocean-400 hover:bg-gray-50 transition-colors"
      >
        <svg className="w-8 h-8 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
        <p className="mt-2 text-sm text-gray-600">Click to upload documents (multiple files allowed)</p>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFilesSelected}
        />
      </div>

      {(existingDocuments.length > 0 || newFiles.length > 0) && (
        <ul className="mt-4 space-y-2">
          {existingDocuments.map((doc, index) => (
            <li key={`existing-${index}`} className="flex items-center justify-between bg-gray-50 rounded-md px-3 py-2 text-sm">
              <a href={fileUrl(doc)} target="_blank" rel="noreferrer" className="text-ocean-600 hover:underline truncate">
                {doc.name || `Document ${index + 1}`}
              </a>
              <button
                type="button"
                onClick={() => onRemoveExisting(index)}
                className="text-red-600 hover:text-red-800 ml-3 flex-shrink-0"
              >
                Remove
              </button>
            </li>
          ))}
          {newFiles.map((file, index) => (
            <li key={`new-${index}`} className="flex items-center justify-between bg-blue-50 rounded-md px-3 py-2 text-sm">
              <span className="text-gray-800 truncate">{file.name} <span className="text-xs text-blue-600">(new)</span></span>
              <button
                type="button"
                onClick={() => onRemoveNewFile(index)}
                className="text-red-600 hover:text-red-800 ml-3 flex-shrink-0"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ContractDocumentsSection;
